/**
 * Letter Patch Upsell
 * ---------------------------------------------------------------------------
 * Shows a "spell it out with letter patches" popup after a customer adds one
 * of the hat-builder products to their cart.
 *
 * The hat builder runs through the third-party Kickflip customizer, which
 * redirects straight to the cart page right after adding — often before this
 * popup would have a chance to render, and always before it would have a
 * chance to render *and stay visible*. So rather than racing that redirect,
 * detection works by deferring:
 *   1. The moment a hat add is detected, a sessionStorage "pending" flag is
 *      set. Nothing is shown yet.
 *   2. The next full page load (almost always the cart page, after
 *      Kickflip's redirect) picks up that flag, confirms it against the
 *      live cart contents, and shows the popup there. Because this runs on
 *      a page that has already finished loading, there's no redirect left
 *      to race against.
 *   3. As a safety net for any future flow that *doesn't* redirect, a short
 *      timer will show the popup on the original page if nothing navigated
 *      away within ~1s of the add.
 * Both paths are guarded by a "shown" flag so the popup only appears once
 * per browsing session.
 */
(function () {
  'use strict';

  // Product handles that should trigger the letter-patch upsell when added
  // to cart. Add a new hat-builder product's handle here if Hillary launches
  // another school hat builder in the future.
  var HAT_BUILDER_HANDLES = [
    'greenwood-hat-builder',
    'cottonwood-hat-builder',
    'custom-hat-colorado-series',
  ];

  var SHOWN_KEY = 'mixmax:letterPatchUpsellShown';
  var PENDING_KEY = 'mixmax:letterPatchUpsellPending';
  var CART_ADD_PATTERN = /\/cart\/add(\.js)?(\?|$)/;

  function getDialog() {
    return document.querySelector('letter-patch-upsell-dialog');
  }

  function alreadyShownThisSession() {
    try {
      return sessionStorage.getItem(SHOWN_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function markShown() {
    try {
      sessionStorage.setItem(SHOWN_KEY, '1');
    } catch (e) {
      /* ignore - private browsing etc. */
    }
  }

  function markPending() {
    try {
      sessionStorage.setItem(PENDING_KEY, '1');
    } catch (e) {
      /* ignore */
    }
  }

  function consumePending() {
    try {
      var isPending = sessionStorage.getItem(PENDING_KEY) === '1';
      sessionStorage.removeItem(PENDING_KEY);
      return isPending;
    } catch (e) {
      return false;
    }
  }

  function showUpsellIfNeeded() {
    if (alreadyShownThisSession()) return;
    var dialog = getDialog();
    if (!dialog || typeof dialog.open !== 'function') return;
    markShown();
    dialog.open();
  }

  function itemsFromCartAddResponse(data) {
    if (!data) return [];
    if (Array.isArray(data.items)) return data.items; // batch /cart/add.js response
    if (data.handle) return [data]; // single-item /cart/add.js response
    return [];
  }

  function hasHatItem(items) {
    return items.some(function (item) {
      return item && HAT_BUILDER_HANDLES.indexOf(item.handle) !== -1;
    });
  }

  // IMPORTANT: this only marks the pending flag - it deliberately does NOT
  // try to open the dialog on this same page load. Kickflip redirects to
  // /cart immediately after a successful add, and that redirect is already
  // in flight by the time we detect the add (both are reacting to the same
  // fetch response), so any attempt to show the dialog here just gets torn
  // down mid-animation by the navigation a moment later. Showing reliably
  // means always waiting for the next full page load - see
  // checkPendingFromPreviousPage below, which is the only place that calls
  // showUpsellIfNeeded().
  function checkAddedItemsForHat(items) {
    if (hasHatItem(items)) {
      markPending();
      // Safety net: if nothing has redirected us away ~1s after the add
      // (e.g. Kickflip's own flow changes, or another integration calls
      // this without redirecting), show the popup right here instead of
      // waiting for a page load that may never come. In the normal Kickflip
      // flow the page has already navigated away well before this fires, so
      // it's a no-op there.
      setTimeout(function () {
        if (consumePending()) {
          showUpsellIfNeeded();
        }
      }, 900);
    }
  }

  // On every relevant page load, check whether a hat add was flagged just
  // before we (possibly) got redirected here, and confirm it against the
  // real cart contents (no race conditions - the page has already loaded,
  // so there's nothing left to race against).
  function checkPendingFromPreviousPage() {
    if (alreadyShownThisSession()) {
      try {
        sessionStorage.removeItem(PENDING_KEY);
      } catch (e) {}
      return;
    }
    if (!consumePending()) return;

    fetch('/cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (cart && cart.items && hasHatItem(cart.items)) {
          showUpsellIfNeeded();
        }
      })
      .catch(function () {});
  }

  // --- Signal 1: this theme's own cart:update event -------------------------
  document.addEventListener('cart:update', function (event) {
    var cart = event && event.detail && event.detail.resource;
    if (cart && cart.items) {
      checkAddedItemsForHat(cart.items);
    }
  });

  // --- Signal 2: patch fetch() to catch any /cart/add(.js) call -------------
  if (window.fetch) {
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = (init && init.method) || (input && input.method) || 'GET';
      var isCartAdd = CART_ADD_PATTERN.test(url) && String(method).toUpperCase() === 'POST';
      var result = originalFetch.apply(this, arguments);

      if (isCartAdd) {
        result
          .then(function (response) {
            if (!response.ok) return;
            response
              .clone()
              .json()
              .then(function (data) {
                checkAddedItemsForHat(itemsFromCartAddResponse(data));
              })
              .catch(function () {});
          })
          .catch(function () {});
      }

      return result;
    };
  }

  // --- Signal 3: patch XMLHttpRequest for apps that don't use fetch --------
  if (window.XMLHttpRequest) {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__isLetterPatchCartAdd =
        CART_ADD_PATTERN.test(url || '') && String(method || '').toUpperCase() === 'POST';
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (this.__isLetterPatchCartAdd) {
        this.addEventListener('load', function () {
          try {
            var data = JSON.parse(this.responseText);
            checkAddedItemsForHat(itemsFromCartAddResponse(data));
          } catch (e) {
            /* not JSON, ignore */
          }
        });
      }
      return originalSend.apply(this, arguments);
    };
  }

  // ---------------------------------------------------------------------------
  // The modal itself
  // ---------------------------------------------------------------------------
  function isOnCartPage() {
    return !!(window.Theme && window.Theme.template && window.Theme.template.name === 'cart');
  }

  function cartUrl() {
    return (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
  }

  // Most a customer can add to a single hat. Keeps the preview strip (and the
  // hat itself) sane, and gives addLetter() a hard stop.
  var MAX_LETTERS_PER_HAT = 12;

  function defineDialog() {
    if (customElements.get('letter-patch-upsell-dialog')) return;

    class LetterPatchUpsellDialog extends HTMLElement {
      connectedCallback() {
        this.dialogEl = this.querySelector('[data-upsell-dialog]');
        // Ordered list, not a Set/Map keyed by variant - letters can repeat
        // (e.g. "EMMA" needs two Ms) and the array order IS the spelling
        // order, which is exactly what fulfillment needs to know. Each
        // entry: { variantId, letter, price }.
        this.sequence = [];
        this._limitTimer = null;

        if (!this.dialogEl) return;

        this.previewChips = this.querySelector('[data-upsell-preview-chips]');
        this.previewEmpty = this.querySelector('[data-upsell-preview-empty]');

        this.querySelectorAll('[data-patch-tile]').forEach(
          function (tile) {
            tile.addEventListener('click', this.addLetter.bind(this, tile));
          }.bind(this)
        );

        var addBtn = this.querySelector('[data-upsell-add]');
        if (addBtn) addBtn.addEventListener('click', this.addSelected.bind(this));

        // Top-right X: just closes, stays put.
        this.querySelectorAll('[data-upsell-close]').forEach(
          function (btn) {
            btn.addEventListener('click', this.close.bind(this));
          }.bind(this)
        );

        // Bottom "Continue to cart": closes and takes them to the cart.
        this.querySelectorAll('[data-upsell-skip]').forEach(
          function (btn) {
            btn.addEventListener('click', this.skipToCart.bind(this));
          }.bind(this)
        );

        this.dialogEl.addEventListener(
          'click',
          function (event) {
            if (event.target === this.dialogEl) this.close();
          }.bind(this)
        );

        this.dialogEl.addEventListener('close', this.onNativeClose.bind(this));

        this.render();
      }

      open() {
        if (this.dialogEl && !this.dialogEl.open) {
          this.dialogEl.showModal();
          document.documentElement.setAttribute('data-letter-patch-upsell-open', '');
        }
      }

      close() {
        if (this.dialogEl && this.dialogEl.open) {
          this.dialogEl.close();
        }
      }

      skipToCart() {
        this.close();
        if (!isOnCartPage()) {
          window.location.href = cartUrl();
        }
      }

      onNativeClose() {
        document.documentElement.removeAttribute('data-letter-patch-upsell-open');
      }

      // Tapping a letter tile always APPENDS one instance of that letter to
      // the sequence - it's how the customer builds the word left to right.
      // To fix a mistake they remove chips (see removeAt) rather than
      // toggling the tile, since a tile no longer maps 1:1 to "selected".
      addLetter(tile) {
        if (tile.hasAttribute('disabled')) return;

        if (this.sequence.length >= MAX_LETTERS_PER_HAT) {
          this.flashNotice('You can add up to ' + MAX_LETTERS_PER_HAT + ' letters on one hat.');
          return;
        }

        var variantId = tile.getAttribute('data-variant-id');
        var letter = tile.getAttribute('data-letter');
        var price = Number(tile.getAttribute('data-price')) || 0;

        this.sequence.push({ variantId: variantId, letter: letter, price: price });
        this.render();
      }

      removeAt(index) {
        this.sequence.splice(index, 1);
        this.render();
      }

      flashNotice(message) {
        var errorEl = this.querySelector('[data-upsell-error]');
        if (!errorEl) return;
        errorEl.textContent = message;
        errorEl.hidden = false;
        if (this._limitTimer) clearTimeout(this._limitTimer);
        this._limitTimer = setTimeout(function () {
          errorEl.hidden = true;
        }, 2400);
      }

      currentWord() {
        return this.sequence
          .map(function (entry) {
            return entry.letter;
          })
          .join('');
      }

      // Single re-render driven entirely by this.sequence: tile badges (so a
      // tile tapped twice shows "×2"), the preview strip of chips in
      // spelling order, and the footer summary/Add button. Called after
      // every add/remove so the three stay in sync.
      render() {
        var counts = {};
        this.sequence.forEach(function (entry) {
          counts[entry.variantId] = (counts[entry.variantId] || 0) + 1;
        });

        this.querySelectorAll('[data-patch-tile]').forEach(function (tile) {
          var variantId = tile.getAttribute('data-variant-id');
          var count = counts[variantId] || 0;
          var badge = tile.querySelector('[data-tile-count]');
          if (count > 0) {
            tile.classList.add('is-selected');
            tile.setAttribute('aria-pressed', 'true');
            if (badge) {
              badge.hidden = false;
              badge.textContent = '×' + count;
            }
          } else {
            tile.classList.remove('is-selected');
            tile.setAttribute('aria-pressed', 'false');
            if (badge) badge.hidden = true;
          }
        });

        if (this.previewChips) {
          this.previewChips.innerHTML = '';
          this.sequence.forEach(
            function (entry, index) {
              var chip = document.createElement('button');
              chip.type = 'button';
              chip.className = 'letter-patch-upsell__chip';
              chip.setAttribute(
                'aria-label',
                'Remove ' + entry.letter + ' (position ' + (index + 1) + ' of ' + this.sequence.length + ')'
              );
              chip.textContent = entry.letter;
              chip.addEventListener('click', this.removeAt.bind(this, index));
              this.previewChips.appendChild(chip);
            }.bind(this)
          );
        }

        if (this.previewEmpty) {
          this.previewEmpty.hidden = this.sequence.length > 0;
        }

        this.updateSummary();
      }

      updateSummary() {
        var count = this.sequence.length;
        var total = this.sequence.reduce(function (sum, entry) {
          return sum + entry.price;
        }, 0);

        var addBtn = this.querySelector('[data-upsell-add]');
        var summaryEl = this.querySelector('[data-upsell-summary]');

        if (summaryEl) {
          summaryEl.textContent =
            count === 0
              ? 'Tap the letters you want to add.'
              : '“' +
                this.currentWord() +
                '” — ' +
                count +
                (count === 1 ? ' letter — $' : ' letters — $') +
                total.toFixed(2);
        }

        if (addBtn) {
          addBtn.disabled = count === 0;
          addBtn.textContent =
            count === 0
              ? 'Add letters'
              : 'Add ' + count + (count === 1 ? ' patch' : ' patches') + ' — $' + total.toFixed(2);
        }
      }

      addSelected() {
        if (this.sequence.length === 0) return;

        var addBtn = this.querySelector('[data-upsell-add]');
        var errorEl = this.querySelector('[data-upsell-error]');
        if (errorEl) errorEl.hidden = true;
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.setAttribute('aria-busy', 'true');
        }

        var word = this.currentWord();
        var total = this.sequence.length;

        // Each patch gets its own line item (quantity 1, even for a
        // repeated letter) tagged with the word it belongs to and its
        // 1-based position in that word. That's what shows up on the order
        // in Shopify admin and on the packing slip, so whoever is
        // assembling the hat can lay the patches out in the right order
        // without having to ask the customer.
        var items = this.sequence.map(function (entry, index) {
          return {
            id: Number(entry.variantId),
            quantity: 1,
            properties: {
              Spells: word,
              Position: index + 1 + ' of ' + total,
            },
          };
        });

        var sectionIds = [];
        document.querySelectorAll('cart-items-component').forEach(function (el) {
          if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
        });

        var cartAddUrl =
          (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

        var self = this;

        fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items, sections: sectionIds.join(',') }),
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (data) {
            if (data && data.status) {
              throw new Error(data.message || 'Could not add patches to cart');
            }

            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: {},
                  sourceId: 'letter-patch-upsell',
                  data: {
                    source: 'letter-patch-upsell',
                    itemCount: items.length,
                    sections: data.sections,
                  },
                },
              })
            );

            self.sequence = [];
            self.render();
            self.close();
            if (!isOnCartPage()) {
              window.location.href = cartUrl();
            }
          })
          .catch(function (err) {
            if (errorEl) {
              errorEl.textContent =
                "Sorry, we couldn't add those patches. You can add them from the Patches page instead.";
              errorEl.hidden = false;
            }
            if (window.console) console.error(err);
          })
          .finally(function () {
            if (addBtn) {
              addBtn.removeAttribute('aria-busy');
            }
            self.updateSummary();
          });
      }
    }

    customElements.define('letter-patch-upsell-dialog', LetterPatchUpsellDialog);
  }

  function init() {
    defineDialog();
    checkPendingFromPreviousPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
